import { foldArabicDigits } from "@a2z/text";
import type {
  DriverSettlementBagView,
  DriverSettlementCardView,
  DriverSettlementOutcome,
  DriverSettlementRequestBody,
} from "@/lib/api/dispatchTypes";

/**
 * THE SETTLE PANEL'S FORM (B6 · P25, plan D-42 / D-43) — display only.
 *
 * The server is the authority on every figure: the panel opens on the card's
 * defaults (whose sums the server computed), and when the cashier changes a
 * bag's outcome the panel re-adds the bags' OWN server amounts (`due`, `total`,
 * `collectedAtPickup`) by the same rules as `driver-settlement.policy.ts`, in
 * whole piastres. The command then sends that expected cash and the server
 * refuses a mismatch (409 STALE with a fresh card) — so a wrong sum here can
 * only ever be refused, never settled.
 */

/** A bag's identity in the form: a bill can be on a run twice (returned, then re-delivered). */
export function bagKey(bag: Pick<DriverSettlementBagView, "invoiceId" | "assignmentId">): string {
  return `${bag.invoiceId}:${bag.assignmentId ?? ""}`;
}

export type Outcomes = ReadonlyMap<string, DriverSettlementOutcome>;

/** The bags the command must list — every one, or the server answers STALE. */
export function runBags(card: DriverSettlementCardView): DriverSettlementBagView[] {
  return card.bags.filter((bag) => bag.onRun);
}

/** Each run bag at its default outcome (the first the server allows). */
export function defaultOutcomes(card: DriverSettlementCardView): Map<string, DriverSettlementOutcome> {
  const out = new Map<string, DriverSettlementOutcome>();
  for (const bag of runBags(card)) {
    if (bag.defaultOutcome) out.set(bagKey(bag), bag.defaultOutcome);
  }
  return out;
}

/**
 * After a STALE refusal the panel moves to the fresh card: a choice the
 * cashier made survives when the bag is still there and still allows it;
 * everything else falls back to the new default.
 */
export function carryOutcomes(card: DriverSettlementCardView, previous: Outcomes): Map<string, DriverSettlementOutcome> {
  const out = defaultOutcomes(card);
  for (const bag of runBags(card)) {
    const chosen = previous.get(bagKey(bag));
    if (chosen && bag.allowedOutcomes.includes(chosen)) out.set(bagKey(bag), chosen);
  }
  return out;
}

/**
 * The bag's outcome: the cashier's choice while the bag still allows it, else
 * its default (S1) — a stale choice (the live card refetched, the bag's
 * allowed set changed) must never be sent, or the server answers
 * OUTCOME_INVALID for ever while the radios, at one option, are hidden.
 */
export function outcomeOf(bag: DriverSettlementBagView, outcomes: Outcomes): DriverSettlementOutcome | null {
  const chosen = outcomes.get(bagKey(bag));
  return chosen && bag.allowedOutcomes.includes(chosen) ? chosen : bag.defaultOutcome;
}

const cents = (amount: number): number => Math.round(amount * 100);
const fromCents = (value: number): number => value / 100;

export type FormSums = {
  /** Σ due of the bags settled in cash — what the driver owes the drawer. */
  cashDue: number;
  /** `cashDue − payout`. */
  cashExpected: number;
  instapayTotal: number;
  aggregatorTotal: number;
  /** Σ bill totals of the bags already paid ("paid by card or before it left"). */
  alreadyPaidTotal: number;
  /** Returned + voided: the cash that was to be collected (not collected). */
  returnedTotal: number;
  leftOwedTotal: number;
};

/** The sums of the run at the chosen outcomes (`settlementSums` of the policy). */
export function formSums(card: DriverSettlementCardView, outcomes: Outcomes, payout = 0): FormSums {
  let cash = 0;
  let instapay = 0;
  let aggregator = 0;
  let alreadyPaid = 0;
  let returned = 0;
  let leftOwed = 0;
  for (const bag of runBags(card)) {
    switch (outcomeOf(bag, outcomes)) {
      case "SETTLE_CASH":
        cash += cents(bag.due);
        break;
      case "SETTLE_INSTAPAY":
        instapay += cents(bag.due);
        break;
      case "SETTLE_AGGREGATOR":
        aggregator += cents(bag.due);
        break;
      case "ALREADY_PAID":
        alreadyPaid += cents(bag.total);
        break;
      case "RETURNED":
      case "VOIDED":
        returned += cents(bag.collectedAtPickup ?? bag.due);
        break;
      case "LEAVE_OWED":
        leftOwed += cents(bag.due);
        break;
      default:
        break;
    }
  }
  return {
    cashDue: fromCents(cash),
    cashExpected: fromCents(cash - cents(payout)),
    instapayTotal: fromCents(instapay),
    aggregatorTotal: fromCents(aggregator),
    alreadyPaidTotal: fromCents(alreadyPaid),
    returnedTotal: fromCents(returned),
    leftOwedTotal: fromCents(leftOwed),
  };
}

/**
 * The typed amount as a number, or null when it is not one: Arabic-Indic
 * digits fold, «٫» / «,» read as the decimal point, at most two decimals,
 * never negative. Blank is null ("Type the cash handed over").
 */
export function parseAmount(text: string): number | null {
  const folded = foldArabicDigits(text).replace(/[٫,]/g, ".").replace(/\s+/g, "");
  if (folded === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(folded) && !/^\.\d{1,2}$/.test(folded)) return null;
  const value = Number(folded);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : null;
}

export type CashDiff =
  | { kind: "match" }
  | { kind: "short"; amount: number }
  | { kind: "over"; amount: number };

/** counted − expected, signed, in whole piastres (D-43). */
export function cashDiff(counted: number, expected: number): CashDiff {
  const difference = cents(counted) - cents(expected);
  if (difference === 0) return { kind: "match" };
  return difference < 0
    ? { kind: "short", amount: fromCents(-difference) }
    : { kind: "over", amount: fromCents(difference) };
}

/** The count's starting text: the driver's pending in-app handover (D-42), or 0 when no cash is expected. */
export function prefillCounted(card: DriverSettlementCardView): string {
  if (card.pendingHandover) return String(card.pendingHandover.amount);
  return card.sums.cashExpected === 0 ? "0" : "";
}

/** The returned / voided bags the cashier must say came back. */
export function returnedBags(card: DriverSettlementCardView): DriverSettlementBagView[] {
  return runBags(card)
    .filter((bag) => bag.state === "RETURNED" || bag.state === "VOIDED")
    .sort((a, b) => a.orderNumber - b.orderNumber);
}

export type SettleGate =
  | { kind: "onTheWay"; count: number }
  | { kind: "nothingToSettle" }
  | { kind: "tickReturns" }
  | { kind: "typeCash" }
  | { kind: "payoutInvalid" }
  | { kind: "payoutTooLarge" }
  | { kind: "ready"; diff: CashDiff };

/**
 * Why Confirm is disabled, in the order the cashier meets the reasons, or
 * `ready` with the difference that picks its label: «Confirm settlement»
 * (matches) or «Settle with difference» (a second tap).
 */
export function settleGate(input: {
  card: DriverSettlementCardView;
  outcomes: Outcomes;
  countedText: string;
  returnsAcknowledged: boolean;
  payoutText: string;
  payoutReason: string;
}): SettleGate {
  const { card } = input;
  // Only a bag coming back blocks: one still out is either collected here
  // (which delivers it) or, paid before it left, simply not on this run.
  if (card.counts.comingBack > 0) return { kind: "onTheWay", count: card.counts.comingBack };
  if (runBags(card).length === 0) return { kind: "nothingToSettle" };
  if (returnedBags(card).length > 0 && !input.returnsAcknowledged) return { kind: "tickReturns" };
  const counted = parseAmount(input.countedText);
  if (counted === null) return { kind: "typeCash" };
  const payout = input.payoutText.trim() === "" ? 0 : parseAmount(input.payoutText);
  if (payout === null || (payout > 0 && input.payoutReason.trim() === "")) return { kind: "payoutInvalid" };
  const sums = formSums(card, input.outcomes, payout);
  if (sums.cashExpected < 0) return { kind: "payoutTooLarge" };
  return { kind: "ready", diff: cashDiff(counted, sums.cashExpected) };
}

/** The T22 body (the envelope's id and timestamp are added by `useDispatchCommand`). */
export function settlementRequest(input: {
  card: DriverSettlementCardView;
  outcomes: Outcomes;
  counted: number;
  returnsAcknowledged: boolean;
  payout: { amount: number; reason: string } | null;
}): DriverSettlementRequestBody {
  const { card } = input;
  const subject: DriverSettlementRequestBody["subject"] = { kind: card.subject.kind };
  if (card.subject.driverSellerId !== null) subject.driverSellerId = card.subject.driverSellerId;
  if (card.subject.riderKey !== null) subject.riderKey = card.subject.riderKey;
  if (card.subject.aggregatorId !== null) subject.aggregatorId = card.subject.aggregatorId;
  const payout = input.payout && input.payout.amount > 0 ? input.payout : null;
  const sums = formSums(card, input.outcomes, payout?.amount ?? 0);
  return {
    subject,
    bags: runBags(card).map((bag) => ({
      invoiceId: bag.invoiceId,
      ...(bag.assignmentId ? { assignmentId: bag.assignmentId } : {}),
      expectedVersion: bag.version,
      outcome: outcomeOf(bag, input.outcomes) as DriverSettlementOutcome,
    })),
    returnedAcknowledged: returnedBags(card).length > 0 ? input.returnsAcknowledged : false,
    countedCash: input.counted,
    expectedCash: sums.cashExpected,
    ...(payout ? { payout: { amount: payout.amount, reason: payout.reason.trim() } } : {}),
  };
}

export type CardChange =
  | { kind: "cash"; from: number; to: number }
  | { kind: "added"; orderNumber: number }
  | { kind: "removed"; orderNumber: number }
  | { kind: "due"; orderNumber: number; from: number; to: number }
  | { kind: "state"; orderNumber: number };

/**
 * What a STALE refusal changed between the card the cashier was looking at
 * and the server's fresh one — shown above the run so the new number is never
 * confirmed blind.
 */
export function cardChanges(before: DriverSettlementCardView, after: DriverSettlementCardView): CardChange[] {
  const changes: CardChange[] = [];
  const old = new Map(runBags(before).map((bag) => [bagKey(bag), bag]));
  const fresh = new Map(runBags(after).map((bag) => [bagKey(bag), bag]));
  for (const [key, bag] of fresh) {
    const was = old.get(key);
    if (!was) changes.push({ kind: "added", orderNumber: bag.orderNumber });
    else if (cents(was.due) !== cents(bag.due)) {
      changes.push({ kind: "due", orderNumber: bag.orderNumber, from: was.due, to: bag.due });
    } else if (was.state !== bag.state || was.defaultOutcome !== bag.defaultOutcome) {
      changes.push({ kind: "state", orderNumber: bag.orderNumber });
    }
  }
  for (const [key, bag] of old) {
    if (!fresh.has(key)) changes.push({ kind: "removed", orderNumber: bag.orderNumber });
  }
  if (cents(before.sums.cashExpected) !== cents(after.sums.cashExpected)) {
    changes.unshift({ kind: "cash", from: before.sums.cashExpected, to: after.sums.cashExpected });
  }
  return changes;
}
